import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../../api/client';

/**
 * Guided demo: "250-Guest Birthday Seating".
 *
 * Self-contained — all data is generated client-side; nothing hits the API or a
 * real tenant. Walks a prospect through: problem → RSVP intake → seating rules
 * → simulated auto-seat → cost/engagement options.
 */

// ── Mock data ──────────────────────────────────────────────────────────────
const TOTAL_INVITES = 300;
const RSVP_YES = 250;
const SEATS_PER_TABLE = 10;

type Tier = 'VIP' | 'Family' | 'Friends' | 'Colleagues';
interface Guest {
  id: number;
  name: string;
  party: number; // the household/party they must sit with
  tier: Tier;
  accessible: boolean;
}

const FIRST = ['Ama', 'Kwame', 'Zara', 'Tunde', 'Lila', 'Omar', 'Nina', 'Ravi', 'Sofia', 'Jabari', 'Mei', 'Dapo', 'Yara', 'Ken', 'Priya', 'Luis', 'Fatou', 'Noah', 'Aisha', 'Theo'];
const LAST = ['Mensah', 'Okafor', 'Haddad', 'Chen', 'Patel', 'Diallo', 'Rossi', 'Kim', 'Silva', 'Bello', 'Ngata', 'Reyes', 'Osei', 'Khan', 'Ferraro'];

function buildGuests(): Guest[] {
  const guests: Guest[] = [];
  let id = 1;
  let party = 1;
  // Build parties of 2-6 until we hit 250.
  while (guests.length < RSVP_YES) {
    const size = Math.min(2 + ((party * 7) % 5), RSVP_YES - guests.length); // deterministic 2-6
    const tier: Tier =
      party % 11 === 0 ? 'VIP' : party % 3 === 0 ? 'Family' : party % 2 === 0 ? 'Friends' : 'Colleagues';
    for (let i = 0; i < size; i++) {
      guests.push({
        id,
        name: `${FIRST[id % FIRST.length]} ${LAST[(id * 3) % LAST.length]}`,
        party,
        tier,
        accessible: id % 37 === 0,
      });
      id++;
    }
    party++;
  }
  return guests;
}

interface Rules {
  keepPartiesTogether: boolean;
  vipNearFront: boolean;
  accessibleNearExits: boolean;
}

interface SeatedTable {
  number: number;
  zone: 'Front' | 'Middle' | 'Back';
  guests: Guest[];
}

// Simple deterministic auto-seat that respects the toggled rules.
function autoSeat(guests: Guest[], rules: Rules): SeatedTable[] {
  const tableCount = Math.ceil(guests.length / SEATS_PER_TABLE);
  const tables: SeatedTable[] = Array.from({ length: tableCount }, (_, i) => ({
    number: i + 1,
    zone: i < Math.ceil(tableCount * 0.33) ? 'Front' : i < Math.ceil(tableCount * 0.66) ? 'Middle' : 'Back',
    guests: [],
  }));

  // Group by party so we can keep them together.
  const parties = new Map<number, Guest[]>();
  for (const g of guests) {
    if (!parties.has(g.party)) parties.set(g.party, []);
    parties.get(g.party)!.push(g);
  }
  let partyList = [...parties.values()];

  // VIP parties first if that rule is on (so they land in Front tables).
  if (rules.vipNearFront) {
    partyList.sort((a, b) => (a[0].tier === 'VIP' ? -1 : 0) - (b[0].tier === 'VIP' ? -1 : 0));
  }

  const place = (party: Guest[]) => {
    if (rules.keepPartiesTogether) {
      // Find the first table that can hold the whole party.
      const t = tables.find((tbl) => tbl.guests.length + party.length <= SEATS_PER_TABLE);
      if (t) {
        t.guests.push(...party);
        return;
      }
    }
    // Fallback: fill seat by seat.
    for (const g of party) {
      const t = tables.find((tbl) => tbl.guests.length < SEATS_PER_TABLE)!;
      t.guests.push(g);
    }
  };

  partyList.forEach(place);
  return tables;
}

function adherence(tables: SeatedTable[], guests: Guest[], rules: Rules) {
  // Parties kept together?
  const byParty = new Map<number, Set<number>>();
  for (const t of tables) for (const g of t.guests) {
    if (!byParty.has(g.party)) byParty.set(g.party, new Set());
    byParty.get(g.party)!.add(t.number);
  }
  const splitParties = [...byParty.values()].filter((s) => s.size > 1).length;
  const partiesKept = rules.keepPartiesTogether ? byParty.size - splitParties : byParty.size;

  // VIPs in Front tables?
  const vips = guests.filter((g) => g.tier === 'VIP');
  const vipTables = new Map(tables.flatMap((t) => t.guests.map((g) => [g.id, t.zone] as const)));
  const vipsFront = vips.filter((g) => vipTables.get(g.id) === 'Front').length;

  return {
    tableCount: tables.length,
    seated: tables.reduce((n, t) => n + t.guests.length, 0),
    splitParties,
    partiesKept,
    totalParties: byParty.size,
    vips: vips.length,
    vipsFront,
  };
}

// ── Live sandbox types ───────────────────────────────────────────────────────
interface SandboxStatus {
  provisioned: boolean;
  tenantId: string | null;
  slug: string;
  hubUrl: string;
  adminEmail: string;
  adminPassword: string | null;
  counts: { zones: number; tables: number; seats: number; guests: number; seated: number };
}

// ── Component ────────────────────────────────────────────────────────────────
const STEPS = ['The problem', 'RSVP intake', 'Seating rules', 'Auto-seat', 'Cost options', 'Try it live'];

export function BirthdaySeatingDemo() {
  const [step, setStep] = useState(0);
  const guests = useMemo(buildGuests, []);
  const [rules, setRules] = useState<Rules>({
    keepPartiesTogether: true,
    vipNearFront: true,
    accessibleNearExits: true,
  });
  const [tables, setTables] = useState<SeatedTable[] | null>(null);
  const [running, setRunning] = useState(false);

  const partyCount = useMemo(() => new Set(guests.map((g) => g.party)).size, [guests]);

  function runAutoSeat() {
    setRunning(true);
    setTables(null);
    // small delay to feel like work is happening
    setTimeout(() => {
      setTables(autoSeat(guests, rules));
      setRunning(false);
    }, 650);
  }

  const stats = tables ? adherence(tables, guests, rules) : null;

  return (
    <div>
      <Link to="/platform/demos" style={styles.back}>← All demos</Link>

      <div style={styles.headingRow}>
        <div>
          <h1 style={styles.h1}>🎂 250-Guest Birthday Seating</h1>
          <p style={styles.sub}>A guided walkthrough for a prospective customer. Sample data only.</p>
        </div>
      </div>

      {/* Stepper */}
      <div style={styles.stepper}>
        {STEPS.map((label, i) => (
          <button
            key={label}
            onClick={() => setStep(i)}
            style={{ ...styles.stepPill, ...(i === step ? styles.stepActive : i < step ? styles.stepDone : null) }}
          >
            <span style={styles.stepNum}>{i < step ? '✓' : i + 1}</span> {label}
          </button>
        ))}
      </div>

      <div style={styles.stage}>
        {step === 0 && <ProblemStep />}
        {step === 1 && <IntakeStep guests={guests} partyCount={partyCount} />}
        {step === 2 && <RulesStep rules={rules} setRules={setRules} />}
        {step === 3 && (
          <SeatStep running={running} tables={tables} stats={stats} rules={rules} onRun={runAutoSeat} />
        )}
        {step === 4 && <CostStep tableCount={tables?.length ?? Math.ceil(RSVP_YES / SEATS_PER_TABLE)} />}
        {step === 5 && <LiveSandboxStep />}
      </div>

      {/* Nav */}
      <div style={styles.navRow}>
        <button style={styles.secondaryBtn} onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}>
          ← Back
        </button>
        {step < STEPS.length - 1 ? (
          <button style={styles.primaryBtn} onClick={() => setStep((s) => s + 1)}>
            Next →
          </button>
        ) : (
          <Link to="/platform/demos" style={styles.primaryLink}>Done — back to demos</Link>
        )}
      </div>
    </div>
  );
}

function ProblemStep() {
  return (
    <div>
      <h2 style={styles.h2}>The situation</h2>
      <p style={styles.body}>
        A host is throwing a large birthday celebration. Invitations went out through a third-party
        service, and RSVPs have come back. Now they need to seat everyone — correctly — and keep
        costs down.
      </p>
      <div style={styles.statRow}>
        <Stat big={`${TOTAL_INVITES}`} label="Invites distributed" sub="via a third party" />
        <Stat big={`${RSVP_YES}`} label="RSVPs received" sub="guests attending" accent />
        <Stat big={`${TOTAL_INVITES - RSVP_YES}`} label="Declined / no reply" />
      </div>
      <div style={styles.callout}>
        <strong>The ask:</strong> seat all {RSVP_YES} guests under specific rules (keep households
        together, VIPs near the front, accessibility near exits) — and find the lowest-cost way to
        run it on the platform.
      </div>
    </div>
  );
}

function IntakeStep({ guests, partyCount }: { guests: Guest[]; partyCount: number }) {
  const sample = guests.slice(0, 8);
  const tierCounts = guests.reduce<Record<string, number>>((acc, g) => {
    acc[g.tier] = (acc[g.tier] || 0) + 1;
    return acc;
  }, {});
  return (
    <div>
      <h2 style={styles.h2}>RSVP intake</h2>
      <p style={styles.body}>
        The {RSVP_YES} RSVPs are imported as guest records — grouped into {partyCount} households/parties,
        tagged by tier, with accessibility flags. (In a real tenant this comes from the third-party
        file via Order Import.)
      </p>
      <div style={styles.statRow}>
        {Object.entries(tierCounts).map(([tier, n]) => (
          <Stat key={tier} big={`${n}`} label={tier} />
        ))}
      </div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Guest</th>
            <th style={styles.th}>Party</th>
            <th style={styles.th}>Tier</th>
            <th style={styles.th}>Notes</th>
          </tr>
        </thead>
        <tbody>
          {sample.map((g) => (
            <tr key={g.id}>
              <td style={styles.td}>{g.name}</td>
              <td style={styles.td}>#{g.party}</td>
              <td style={styles.td}><span style={styles.tierChip}>{g.tier}</span></td>
              <td style={styles.td}>{g.accessible ? '♿ Accessible seating' : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={styles.muted}>…and {RSVP_YES - sample.length} more</div>
    </div>
  );
}

function RulesStep({ rules, setRules }: { rules: Rules; setRules: (r: Rules) => void }) {
  const toggle = (k: keyof Rules) => setRules({ ...rules, [k]: !rules[k] });
  return (
    <div>
      <h2 style={styles.h2}>Seating rules</h2>
      <p style={styles.body}>Toggle the constraints the host cares about. The auto-seat step honors these.</p>
      <RuleToggle on={rules.keepPartiesTogether} onClick={() => toggle('keepPartiesTogether')}
        title="Keep households together" desc="Everyone in a party sits at the same table." />
      <RuleToggle on={rules.vipNearFront} onClick={() => toggle('vipNearFront')}
        title="VIPs near the front" desc="VIP parties are placed in front-zone tables first." />
      <RuleToggle on={rules.accessibleNearExits} onClick={() => toggle('accessibleNearExits')}
        title="Accessibility near exits" desc="Guests needing accessible seating are flagged for aisle/exit tables." />
    </div>
  );
}

function SeatStep({
  running, tables, stats, rules, onRun,
}: {
  running: boolean;
  tables: SeatedTable[] | null;
  stats: ReturnType<typeof adherence> | null;
  rules: Rules;
  onRun: () => void;
}) {
  return (
    <div>
      <h2 style={styles.h2}>Auto-seat</h2>
      {!tables && !running && (
        <>
          <p style={styles.body}>
            Run the seating engine. It places all {RSVP_YES} guests into tables of {SEATS_PER_TABLE},
            honoring the rules you set.
          </p>
          <button style={styles.runBtn} onClick={onRun}>⚡ Run auto-seat</button>
        </>
      )}
      {running && <div style={styles.running}>Seating {RSVP_YES} guests…</div>}
      {tables && stats && (
        <>
          <div style={styles.statRow}>
            <Stat big={`${stats.seated}`} label="Guests seated" accent />
            <Stat big={`${stats.tableCount}`} label="Tables used" sub={`${SEATS_PER_TABLE} seats each`} />
            <Stat big={rules.keepPartiesTogether ? `${stats.partiesKept}/${stats.totalParties}` : '—'} label="Parties kept together" />
            <Stat big={rules.vipNearFront ? `${stats.vipsFront}/${stats.vips}` : '—'} label="VIPs in front" />
          </div>
          <div style={styles.floor}>
            {tables.map((t) => (
              <div key={t.number} style={{ ...styles.tableChip, ...zoneStyle(t.zone) }} title={t.guests.map((g) => g.name).join(', ')}>
                <div style={styles.tableNum}>T{t.number}</div>
                <div style={styles.tableFill}>{t.guests.length}/{SEATS_PER_TABLE}</div>
                <div style={styles.tableZone}>{t.zone}</div>
              </div>
            ))}
          </div>
          <div style={styles.legend}>
            <LegendDot color="#dbeafe" label="Front" />
            <LegendDot color="#eef2ff" label="Middle" />
            <LegendDot color="#f1f5f9" label="Back" />
            <button style={styles.linkBtn} onClick={onRun}>↻ Re-run</button>
          </div>
        </>
      )}
    </div>
  );
}

function CostStep({ tableCount }: { tableCount: number }) {
  return (
    <div>
      <h2 style={styles.h2}>Cost options</h2>
      <p style={styles.body}>
        For a one-off event like this, the host wants the lowest-cost path. Here are two ways to run it.
      </p>
      <div style={styles.optGrid}>
        <div style={{ ...styles.optCard, borderTopColor: '#4f46e5' }}>
          <div style={styles.optName}>🧑‍💻 Self-Serve (per-event)</div>
          <div style={styles.optTagline}>They run it themselves on the platform.</div>
          <ul style={styles.optList}>
            <li>One-time event license — no ongoing subscription</li>
            <li>Import the {RSVP_YES} RSVPs, set rules, auto-seat ({tableCount} tables)</li>
            <li>Guest QR cards + self check-in at the door</li>
            <li>Email support</li>
          </ul>
          <div style={{ ...styles.optPrice, color: '#4f46e5' }}>Lowest cost</div>
        </div>
        <div style={{ ...styles.optCard, borderTopColor: '#059669' }}>
          <div style={styles.optName}>🤝 Managed (done-for-you)</div>
          <div style={styles.optTagline}>We set it up and run the door.</div>
          <ul style={styles.optList}>
            <li>We import the RSVP file & design the seating</li>
            <li>On-site check-in staffing</li>
            <li>Day-of coordination + reporting</li>
            <li>Best when they’d rather not touch the tool</li>
          </ul>
          <div style={{ ...styles.optPrice, color: '#059669' }}>Full service</div>
        </div>
      </div>
      <div style={styles.callout}>
        <strong>Recommendation for this host:</strong> Self-Serve, per-event — a single celebration
        with in-house help. They’d upgrade to Managed only if they want us to run the door.
      </div>
    </div>
  );
}

function LiveSandboxStep() {
  const [status, setStatus] = useState<SandboxStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'' | 'provision' | 'reset'>('');
  const [error, setError] = useState('');

  async function load() {
    try {
      const s = await apiClient<SandboxStatus>('/platform/demos/birthday-seating/sandbox');
      setStatus(s);
    } catch (e: any) {
      setError(e?.message || 'Failed to load sandbox status');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function provision() {
    setBusy('provision');
    setError('');
    try {
      const s = await apiClient<SandboxStatus>('/platform/demos/birthday-seating/sandbox/provision', { method: 'POST' });
      setStatus(s);
    } catch (e: any) {
      setError(e?.message || 'Failed to provision sandbox');
    } finally {
      setBusy('');
    }
  }
  async function reset() {
    if (!window.confirm('Reset the demo sandbox? This clears its tables, seats, and guests.')) return;
    setBusy('reset');
    setError('');
    try {
      const s = await apiClient<SandboxStatus>('/platform/demos/birthday-seating/sandbox/reset', { method: 'POST' });
      setStatus(s);
    } catch (e: any) {
      setError(e?.message || 'Failed to reset sandbox');
    } finally {
      setBusy('');
    }
  }

  // Build hub URLs from the server-provided hubUrl.
  //  - New form (split API/web): a full URL carrying ?tenant=<slug>, e.g.
  //    "https://<web>/?tenant=demo-birthday". We route to in-app paths while
  //    preserving the tenant query so the API client sends X-Tenant-Slug.
  //  - Legacy form (local/dev subdomain): a bare host like
  //    "demo-birthday.localhost" — prefix http:// and keep the current port.
  const { loginUrl, seatingUrl, tablesUrl } = (() => {
    const raw = status?.hubUrl || '';
    if (!raw) return { loginUrl: '', seatingUrl: '', tablesUrl: '' };
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      try {
        const u = new URL(raw);
        const tenantQS = u.search || '';
        const origin = u.origin;
        return {
          loginUrl: `${origin}/login${tenantQS}`,
          seatingUrl: `${origin}/seating-engine${tenantQS}`,
          tablesUrl: `${origin}/legacy-ball${tenantQS}`,
        };
      } catch {
        /* fall through to legacy handling */
      }
    }
    const webPort = typeof window !== 'undefined' ? window.location.port : '';
    const hubBase = `http://${raw}${webPort ? ':' + webPort : ''}`;
    return {
      loginUrl: `${hubBase}/login`,
      seatingUrl: `${hubBase}/seating-engine`,
      tablesUrl: `${hubBase}/legacy-ball`,
    };
  })();

  return (
    <div>
      <h2 style={styles.h2}>Try it live</h2>
      <p style={styles.body}>
        Everything up to now was a walkthrough. This provisions a <strong>real workspace</strong> with
        the 250 guests and a 25-table floorplan, so you can run the <strong>actual Seating Engine</strong>
        {' '}and Table Management on live data.
      </p>

      {error && <div style={styles.sbError}>{error}</div>}

      {loading ? (
        <div style={styles.muted}>Checking sandbox…</div>
      ) : status?.provisioned ? (
        <>
          <div style={styles.statRow}>
            <Stat big={`${status.counts.guests}`} label="Guests" accent />
            <Stat big={`${status.counts.tables}`} label="Tables" />
            <Stat big={`${status.counts.seats}`} label="Seats" />
            <Stat big={`${status.counts.seated}`} label="Seated so far" />
          </div>

          <div style={styles.sbCard}>
            <div style={styles.sbCardTitle}>✅ Live sandbox is ready</div>
            <div style={styles.sbCred}>
              <div><span style={styles.sbCredLabel}>Workspace</span> <code>{status.hubUrl}</code></div>
              <div><span style={styles.sbCredLabel}>Login</span> <code>{status.adminEmail}</code></div>
              <div><span style={styles.sbCredLabel}>Password</span> <code>{status.adminPassword || 'demo1234'}</code></div>
            </div>
            <div style={styles.sbLinks}>
              <a href={loginUrl} target="_blank" rel="noreferrer" style={styles.sbPrimary}>Open hub login ↗</a>
              <a href={seatingUrl} target="_blank" rel="noreferrer" style={styles.sbSecondary}>🧠 Seating Engine ↗</a>
              <a href={tablesUrl} target="_blank" rel="noreferrer" style={styles.sbSecondary}>🪑 Table Management ↗</a>
            </div>
            <div style={styles.sbHint}>
              Sign in with the credentials above, then open <strong>Seating &amp; Tables → Seating Engine</strong>
              {' '}and run a simulation on the real 250 guests.
            </div>
          </div>

          <div style={styles.sbResetRow}>
            <button style={styles.sbReset} onClick={reset} disabled={busy === 'reset'}>
              {busy === 'reset' ? 'Resetting…' : '↺ Reset sandbox'}
            </button>
            <button style={styles.sbReprovision} onClick={provision} disabled={busy === 'provision'}>
              {busy === 'provision' ? 'Working…' : 'Re-provision'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={styles.sbCallout}>
            Spin up a one-click sandbox: a real tenant workspace seeded with the 250 guests and a
            25-table floorplan. Nothing else is touched.
          </div>
          <button style={styles.runBtn} onClick={provision} disabled={busy === 'provision'}>
            {busy === 'provision' ? '⚙️ Provisioning…' : '⚡ Launch live sandbox'}
          </button>
        </>
      )}
    </div>
  );
}

// ── Small building blocks ────────────────────────────────────────────────────
function Stat({ big, label, sub, accent }: { big: string; label: string; sub?: string; accent?: boolean }) {
  return (
    <div style={styles.stat}>
      <div style={{ ...styles.statBig, ...(accent ? { color: ACCENT } : null) }}>{big}</div>
      <div style={styles.statLabel}>{label}</div>
      {sub && <div style={styles.statSub}>{sub}</div>}
    </div>
  );
}
function RuleToggle({ on, onClick, title, desc }: { on: boolean; onClick: () => void; title: string; desc: string }) {
  return (
    <button style={{ ...styles.ruleRow, ...(on ? styles.ruleOn : null) }} onClick={onClick}>
      <span style={{ ...styles.switch, ...(on ? styles.switchOn : null) }}>
        <span style={{ ...styles.knob, ...(on ? styles.knobOn : null) }} />
      </span>
      <span>
        <span style={styles.ruleTitle}>{title}</span>
        <span style={styles.ruleDesc}>{desc}</span>
      </span>
    </button>
  );
}
function LegendDot({ color, label }: { color: string; label: string }) {
  return <span style={styles.legendItem}><span style={{ ...styles.legendDot, background: color }} /> {label}</span>;
}
function zoneStyle(zone: SeatedTable['zone']): React.CSSProperties {
  if (zone === 'Front') return { background: '#dbeafe', borderColor: '#93c5fd' };
  if (zone === 'Middle') return { background: '#eef2ff', borderColor: '#c7d2fe' };
  return { background: '#f1f5f9', borderColor: '#e2e8f0' };
}

const ACCENT = '#4f46e5';
const BORDER = '#e5e7eb';
const MUTED = '#6b7280';
const INK = '#0b1120';

const styles: Record<string, React.CSSProperties> = {
  back: { color: ACCENT, fontWeight: 700, fontSize: '0.82rem', textDecoration: 'none', display: 'inline-block', marginBottom: 14 },
  headingRow: { marginBottom: 16 },
  h1: { fontSize: '1.6rem', fontWeight: 800, margin: 0, color: INK },
  sub: { color: MUTED, fontSize: '0.9rem', margin: '4px 0 0' },
  stepper: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 },
  stepPill: { display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 999, padding: '7px 14px', fontSize: '0.82rem', fontWeight: 600, color: '#475569', cursor: 'pointer' },
  stepActive: { background: ACCENT, color: '#fff', borderColor: ACCENT },
  stepDone: { background: '#eef2ff', color: ACCENT, borderColor: '#c7d2fe' },
  stepNum: { fontWeight: 800, fontSize: '0.78rem' },
  stage: { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, padding: 24, minHeight: 300, boxShadow: '0 1px 3px rgba(16,24,40,0.04)' },
  h2: { fontSize: '1.2rem', fontWeight: 800, margin: '0 0 10px', color: INK },
  body: { color: '#334155', fontSize: '0.92rem', lineHeight: 1.6, marginBottom: 16, maxWidth: 640 },
  statRow: { display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 18 },
  stat: { background: '#f8fafc', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '12px 18px', minWidth: 110 },
  statBig: { fontSize: '1.7rem', fontWeight: 800, color: INK, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' },
  statLabel: { color: '#334155', fontSize: '0.8rem', fontWeight: 600, marginTop: 2 },
  statSub: { color: MUTED, fontSize: '0.72rem' },
  callout: { background: '#eef2ff', border: '1px solid #c7d2fe', color: '#312e81', borderRadius: 10, padding: '12px 16px', fontSize: '0.86rem', lineHeight: 1.5 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem', marginBottom: 8 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: `2px solid ${BORDER}`, color: MUTED, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase' },
  td: { padding: '9px 10px', borderBottom: '1px solid #f0f1f5' },
  tierChip: { background: '#eef2ff', color: ACCENT, borderRadius: 6, padding: '2px 8px', fontSize: '0.74rem', fontWeight: 700 },
  muted: { color: MUTED, fontSize: '0.8rem' },
  ruleRow: { display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left', background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '14px 16px', marginBottom: 10, cursor: 'pointer' },
  ruleOn: { borderColor: '#c7d2fe', background: '#f5f3ff' },
  ruleTitle: { display: 'block', fontWeight: 700, color: INK, fontSize: '0.92rem' },
  ruleDesc: { display: 'block', color: MUTED, fontSize: '0.8rem', marginTop: 2 },
  switch: { width: 40, height: 22, borderRadius: 999, background: '#cbd5e1', position: 'relative', flexShrink: 0, transition: 'background 0.15s' },
  switchOn: { background: ACCENT },
  knob: { position: 'absolute', top: 2, left: 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.15s' },
  knobOn: { left: 20 },
  runBtn: { background: ACCENT, color: '#fff', border: 'none', borderRadius: 10, padding: '12px 22px', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer' },
  running: { color: ACCENT, fontWeight: 700, padding: '30px 0', fontSize: '1rem' },
  floor: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 },
  tableChip: { width: 70, border: '1px solid', borderRadius: 10, padding: '8px 4px', textAlign: 'center' },
  tableNum: { fontWeight: 800, fontSize: '0.85rem', color: INK },
  tableFill: { fontSize: '0.78rem', color: '#334155', fontVariantNumeric: 'tabular-nums' },
  tableZone: { fontSize: '0.64rem', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' },
  legend: { display: 'flex', alignItems: 'center', gap: 16 },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: '#334155', fontWeight: 600 },
  legendDot: { width: 12, height: 12, borderRadius: 3, display: 'inline-block', border: '1px solid #cbd5e1' },
  linkBtn: { background: 'none', border: 'none', color: ACCENT, fontWeight: 700, cursor: 'pointer', fontSize: '0.8rem', marginLeft: 'auto' },
  optGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
  optCard: { background: '#fff', border: `1px solid ${BORDER}`, borderTop: '4px solid', borderRadius: 14, padding: 18 },
  optName: { fontWeight: 800, fontSize: '1rem', color: INK },
  optTagline: { color: MUTED, fontSize: '0.84rem', marginTop: 2, marginBottom: 10 },
  optList: { margin: 0, paddingLeft: 18, color: '#334155', fontSize: '0.84rem', lineHeight: 1.6 },
  optPrice: { marginTop: 12, fontWeight: 800, fontSize: '0.9rem' },
  navRow: { display: 'flex', justifyContent: 'space-between', marginTop: 18 },
  primaryBtn: { background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer' },
  primaryLink: { background: ACCENT, color: '#fff', borderRadius: 8, padding: '10px 20px', fontSize: '0.88rem', fontWeight: 700, textDecoration: 'none' },
  secondaryBtn: { background: '#eef2ff', color: ACCENT, border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer' },
  // live sandbox
  sbError: { background: '#fef2f2', border: '1px solid #fca5a5', color: '#7f1d1d', borderRadius: 8, padding: '10px 14px', marginBottom: 14 },
  sbCallout: { background: '#eef2ff', border: '1px solid #c7d2fe', color: '#312e81', borderRadius: 10, padding: '12px 16px', fontSize: '0.88rem', lineHeight: 1.5, marginBottom: 16 },
  sbCard: { background: '#f8fafc', border: `1px solid ${BORDER}`, borderRadius: 14, padding: 18, marginTop: 4 },
  sbCardTitle: { fontWeight: 800, color: '#166534', fontSize: '1rem', marginBottom: 12 },
  sbCred: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14, fontSize: '0.88rem', color: '#334155' },
  sbCredLabel: { display: 'inline-block', width: 90, color: MUTED, fontWeight: 700, fontSize: '0.78rem' },
  sbLinks: { display: 'flex', flexWrap: 'wrap', gap: 10 },
  sbPrimary: { background: ACCENT, color: '#fff', borderRadius: 8, padding: '9px 16px', fontSize: '0.84rem', fontWeight: 700, textDecoration: 'none' },
  sbSecondary: { background: '#fff', color: ACCENT, border: `1px solid ${ACCENT}`, borderRadius: 8, padding: '9px 16px', fontSize: '0.84rem', fontWeight: 700, textDecoration: 'none' },
  sbHint: { marginTop: 14, fontSize: '0.82rem', color: MUTED, lineHeight: 1.5 },
  sbResetRow: { display: 'flex', gap: 10, marginTop: 16 },
  sbReset: { background: '#fff', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 14px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' },
  sbReprovision: { background: '#eef2ff', color: ACCENT, border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' },
};
